// How full the Inbox was, and how much came in and was settled, worked out from the to-dos themselves (InboxHistory.swift,
// with Store.inboxLevel: the figures under the Inbox's title). Nothing runs in the background: every change to the Inbox
// leaves its date on the to-do (`leftInboxAt`, or `completedAt` for one ticked off in the Inbox) or, for one deleted
// undecided, a trace. To-dos that left the Inbox before history was recorded are not counted. The cases are in
// cases/inbox-figures.json, which the kit's SharedCasesInboxFiguresTests read too.

import { dayOf, daysBetween, startOfDay, weekStart } from "./days";
import type { InboxTrace, Todo } from "./records";
import type { Day, Weekday } from "./types";

/** One stay in the Inbox: when it came, and when it left (undefined while it is still there). */
export interface InboxStay {
  came: Date;
  left?: Date;
}

/** Every stay the records know of (InboxHistory.init). */
export function inboxStays(todos: readonly Pick<Todo, "start" | "createdAt" | "completedAt" | "leftInboxAt">[], traces: readonly Pick<InboxTrace, "createdAt" | "leftAt">[]): InboxStay[] {
  const stays: InboxStay[] = [];
  for (const todo of todos) {
    if (todo.start.kind === "inbox") stays.push(todo.completedAt ? { came: todo.createdAt, left: todo.completedAt } : { came: todo.createdAt });
    else if (todo.leftInboxAt) stays.push({ came: todo.createdAt, left: todo.leftInboxAt });
  }
  for (const trace of traces) stays.push({ came: trace.createdAt, left: trace.leftAt });
  return stays;
}

/** Items in the Inbox at the end of `day` (InboxHistory.level: the day's start and 24 hours, as the kit has it). */
export function inboxLevel(stays: readonly InboxStay[], day: Day, zone: string): number {
  const end = startOfDay(day, zone).getTime() + 86_400_000;
  return stays.filter((stay) => stay.came.getTime() < end && (stay.left === undefined || stay.left.getTime() >= end)).length;
}

/** What came in and what was settled between two moments. */
export function inboxFlow(stays: readonly InboxStay[], start: Date, end: Date): { new: number; settled: number } {
  const within = (moment: Date | undefined) => moment !== undefined && moment.getTime() >= start.getTime() && moment.getTime() < end.getTime();
  return { new: stays.filter((stay) => within(stay.came)).length, settled: stays.filter((stay) => within(stay.left)).length };
}

/** Days, by the calendar in the zone, the oldest item still in the Inbox has waited; undefined when none waits. */
export function oldestWait(stays: readonly InboxStay[], now: Date, zone: string): number | undefined {
  const waiting = stays.filter((stay) => stay.left === undefined).map((stay) => stay.came.getTime());
  if (waiting.length === 0) return undefined;
  return daysBetween(dayOf(new Date(Math.min(...waiting)), zone), dayOf(now, zone));
}

/** The figures under the Inbox's title, before they are put into words. */
export interface InboxFigures {
  /** Open to-dos in the Inbox: "4 to decide". */
  open: number;
  /** "the oldest has waited 3 days": only when more than one day. */
  waited?: number;
  /** "This week: 15 new, 11 settled": only when something came or went this week. */
  week?: { new: number; settled: number };
}

/**
 * Store.inboxLevel: undefined while the Inbox is empty. The week runs from the start of the week (`firstWeekday`, as in
 * Calendar: 2 = Monday) in the zone to now. `open` is the Inbox's count as its list shows it (Lists.count); without
 * it, the stays still there.
 */
export function inboxFigures(stays: readonly InboxStay[], now: Date, zone: string, options: { open?: number; firstWeekday?: Weekday } = {}): InboxFigures | undefined {
  const open = options.open ?? stays.filter((stay) => stay.left === undefined).length;
  if (open <= 0) return undefined;
  const figures: InboxFigures = { open };
  const waited = oldestWait(stays, now, zone);
  if (waited !== undefined && waited > 1) figures.waited = waited;
  const flow = inboxFlow(stays, startOfDay(weekStart(dayOf(now, zone), options.firstWeekday ?? 2), zone), now);
  if (flow.new > 0 || flow.settled > 0) figures.week = flow;
  return figures;
}

